import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Logo from '../../UI/Logo';
import UIButton from '../../UI/UIButton';
import {
  wh,
  MediumTitle,
  fadeInAnimation,
  screenPadding,
  fontSize,
  centeredFlex,
} from '../../mixins';
import { useTranslation } from 'react-i18next';
import SignupInput from './SignupInput';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEmail,
  selectSuccess,
  selectUserUuid,
  selectValidity,
  sendAPIRequest,
} from './signupSlice';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import { selectEventsData } from '../events/eventsSlice';
import {
  appendLink,
  checkoutImagesLinks,
  plansImagesLinks,
  checkoutWebpImagesLinks,
  plansWebpImagesLinks,
} from '../../helper/utils';
import { browserName, isBrowser } from 'react-device-detect';
import useWindowWidth from '../../helper/useWindowWidth';
import { selectWebp } from '../ui/uiSlice';
import { selectLandingType } from '../plans/plansSlice';
import RedditPixel from 'react-reddit-pixel';

const StyledSignup = styled.div`
  ${centeredFlex};
  ${screenPadding};
  min-height: 60%;
  ${isBrowser && fadeInAnimation};
  justify-content: flex-end;
  
  img {
    margin: ${props => props.windowWidth <= 430 && props.isFB ? '130px 0 0' : '0 0 50px'};
  }
  
  @media screen and (max-width: 430px) {
    justify-content: ${props => props.isFB ? 'flex-start' : 'flex-end'};
  }
  
`;

const Title = styled.h1`
  ${MediumTitle};
  margin-top: 0px;
  margin-bottom: 30px;
`;

export const StyledBtn = styled.button`
  background-color: transparent;
  border: none;
  display: inline-block;
  color: #fff;
  cursor: pointer;
  ${fontSize(12)};
  line-height: 18px;
  letter-spacing: -0.128px;
  font-weight: 300;
  text-decoration: underline;
  
  &:focus,
  &:active {
    outline: none;
  }
`;

const StyledText = styled.p`
  ${fontSize(12)};
  ${wh('90%', 'auto')};
  line-height: 18px;
  color: #fff;
  letter-spacing: -0.128px;
  text-align: center;
  font-weight: 300;
  margin: 0 0 24px;
  
  a {
    text-decoration: underline;
  }
`;

const ContentBlock = styled.div`
  ${centeredFlex};
  width: 100%;
`;

const HiddenInput = styled.input`
  position: absolute;
  top: -9999px;
`;

const SignupScreen = ({history}) => {
  const { t } = useTranslation();
  const isValid = useSelector(selectValidity);
  const email = useSelector(selectEmail);
  const isSuccess = useSelector(selectSuccess);
  const landingType = useSelector(selectLandingType);
  const dispatch = useDispatch();
  const eventsData = useSelector(selectEventsData);
  const isWebp = useSelector(selectWebp);
  const userUuid = useSelector(selectUserUuid);
  const [initEventSent,setInitEventStatus] = useState(false);
  const windowWidth = useWindowWidth();
  const isFB = (landingType === 'option_c' || landingType === 'option_b2') && browserName === 'Facebook';
  const emailInput = useRef(null);

  const onBtnClick = () => {
    sendAnalyticsEvents(EVENTS.signupRequestSent, eventsData);
    dispatch(sendAPIRequest(email, null, userUuid));
  };

  useEffect(() => {
    isWebp
      ? checkoutWebpImagesLinks.map((href) => appendLink(href)) &&
        plansWebpImagesLinks.map((href) => appendLink(href))
      : plansImagesLinks.map((href) => appendLink(href)) &&
        checkoutImagesLinks.map((href) => appendLink(href))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initEventSent) return;

    sendAnalyticsEvents(EVENTS.signupScreenShown, eventsData);
    setInitEventStatus(true);
  }, [initEventSent, eventsData]);

  useEffect(() => {
    if (isSuccess) {
      RedditPixel.track('SignUp', {'user_email': email});
      sendAnalyticsEvents(EVENTS.signupSuccess, eventsData);
      isFB ?  history.push('/signup/creating-your-profile') : history.push('/signup/set-your-options')
    }
  }, [isSuccess, history, eventsData, isFB, email]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (emailInput.current) {
        emailInput.current.focus();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  const onLinkClick = url => window.open(url);

  return (
    <>
      {isFB && <HiddenInput ref={emailInput} />}
      <StyledSignup isFB={isFB} windowWidth={windowWidth}>
        {(windowWidth > 430 || (windowWidth <= 430 && !isFB)) && <Logo/>}
        <ContentBlock>
          <Title>{t('whats_your_email')}</Title>
          <SignupInput/>
          <StyledText>
            <span>{t('privacy_note')}</span>
            <StyledBtn onClick={() => onLinkClick('https://family-locator.com/terms-of-use/')} id="link-to-terms">{t('terms')}</StyledBtn>
            <span>{t('and')}</span>
            <StyledBtn onClick={() => onLinkClick('https://family-locator.com/privacy-policy/')} id="link-to-privacy">{t('privacy')}</StyledBtn>
          </StyledText>
          <UIButton onClick={onBtnClick} disabled={!isValid}>{t('continue')}</UIButton>
        </ContentBlock>
        {windowWidth <= 430 && isFB && <Logo/>}
      </StyledSignup>
    </>
  );
};

export default SignupScreen;